import {IBirthDateEntity, IDateISO} from './IUtils';

/**
 * Returns all days from 1 to 31 for birth <select> field
 * @returns {IBirthDateEntity[]} all days from 1 to 31
 */
export const getDaysOfBirth = function(): IBirthDateEntity[] {
	const daysList: IBirthDateEntity[] = [];

	for (let day = 1; day <= 31; day++) {
		const value: string = formatNumberForDate(day);

		daysList.push({
			value: value,
			label: value,
		});
	}

	return daysList;
};

/**
 * Returns all months from 1 to 12 for birth <select> field
 * @returns {IBirthDateEntity[]} - all days from 1 to 12
 */
export const getMonthsOfBirth = function(): IBirthDateEntity[] {
	const monthsList: IBirthDateEntity[] = [];

	for (let month = 1; month <= 12; month++) {
		const value: string = formatNumberForDate(month);

		monthsList.push({
			value: value,
			label: value,
		});
	}

	return monthsList;
};

/**
 * Returns range of birth years
 * @param {number} minAge - min age to show years
 * @param {number} maxAge - max age to show years
 * @returns {IBirthDateEntity[]} - years range
 */
export const getYearsOfBirth = function(minAge: number = 6, maxAge: number = 100): IBirthDateEntity[] {
	const currentYear: number = new Date().getFullYear();
	const yearsList: IBirthDateEntity[] = [];

	for (let year = currentYear - minAge; year >= currentYear - maxAge; year--) {
		yearsList.push({
			value: year.toString(),
			label: year.toString(),
		});
	}

	return yearsList;
};

/**
 * Validates date if it is correct
 * @param {object} dateObject - date object
 * @param {number} dateObject.day - nunber of day
 * @param {number} dateObject.month - nunber of month
 * @param {number} dateObject.year - nunber of year
 * @returns {boolean} - return if the date object is valid to build date
 */
export const validateDate = function(dateObject: IDateISO): boolean {
	if (Number.isInteger(dateObject.day) && Number.isInteger(dateObject.month) && Number.isInteger(dateObject.year)) {
		const realDate: Date = new Date(dateObject.year, dateObject.month - 1, dateObject.day);

		return (
			realDate.getDate() === dateObject.day &&
			realDate.getMonth() === dateObject.month - 1 &&
			realDate.getFullYear() === dateObject.year
		);
	}

	return false;
};

/**
 * Formats number to date format
 * @param {number} number - number to format
 * @returns formatted numberS
 */
export function formatNumberForDate(number: number): string {
	const string = number.toString();

	return string.length === 1 ? '0' + string : string;
}

/**
 * Formats date to ISO string format
 * @param {IDateISO} date - date to format
 * @returns {string} formatted date
 */
export function formatDateToISOString(date: IDateISO): string {
	return Object.keys(date).length === 3 && !isNaN(date.year) && !isNaN(date.month) && !isNaN(date.day)
		? `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
		: null;
}

/**
 * Formats date to ISO string format
 * @param {IDateISO} date - date to format
 * @param {string} dateFormat - date date
 * @returns {string} formatted date
 */
export function formatDateWithPattern(date: IDateISO, dateFormat: string): string {
	let inputValueDate = dateFormat;

	Object.keys(date || []).forEach((key) => {
		inputValueDate = inputValueDate?.replace(key, date[key]);
	});

	return date ? inputValueDate : '';
}

/**
 * Sorts array by date
 * @param {object[]} arr - array
 * @param {string} key - key
 * @param {newest} newest - newest
 * @returns {object[]} sorted array
 */
export function sortArrayByDate(arr, key, newest = true): {arr: object[]; key?: string; newest?: boolean} {
	if (!arr) {
		return;
	} else if (arr.length > 1) {
		arr.sort((a, b) => {
			const dateA = new Date(a[key]).getTime();
			const dateB = new Date(b[key]).getTime();

			return newest ? dateB - dateA : dateA - dateB;
		});
	}

	return arr;
}

/**
 * Converts the week day number (1 - 7) to a string day (Monday, Tuesday, ...) in the language passed as param
 * @param {number} dayNumber - The number of the day
 * @param {string} language - The language code
 * @returns {string} - Name of the day
 */
export const getDayString = (dayNumber: number, language: string): string => {
	const date = new Date();
	const currentDay = date.getDay() || 7;

	const difference = ((dayNumber - currentDay) % 7) || 7;
	date.setDate(date.getDate() + difference);

	return new Intl.DateTimeFormat(language, {weekday: 'long'}).format(date);
};

/**
 * Converts a time in format HH:mm to minutes
 * @param {string} timeString - The number of the day
 * @returns {number} - Time in minutes
 */
export const timeStringToMinutes = (timeString: string): number => {
	const [hour, minute] = timeString.split(':').map(Number);

	return hour * 60 + minute;
};

/**
 * Gets the hour of a date in a different timezone in format HH:mm
 * @param {Date} date - The date to convert
 * @param {string} timeZone - The timezone
 * @param {string} locale - the locale for formating the time
 * @returns {string} - Time in minutes
 */
export const getDifferentTimezoneTime = (date: Date, timeZone: string, locale?: string): string => {
	return new Intl.DateTimeFormat(locale, {
		hour: 'numeric', minute: 'numeric', second: 'numeric',
		timeZone: timeZone
	}).format(date).slice(0, 5);
};
